import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { getTicketTypes, lookupTicketEmail, createPublicTicket } from "../api";
import { LifeBuoy, Loader2, CheckCircle2, Building2, Search } from "lucide-react";
import Footer from "../components/Footer";
import Header from "../components/Header";
import TicketTracking from "../components/tickets/TicketTracking";
import { log } from "../lib/log";

const inputCls = "w-full border border-neutral-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-brand bg-white";

export const TopBar = () => <Header solid />;

const TrackForm = () => {
  const nav = useNavigate();
  const [ref, setRef] = useState(""); const [code, setCode] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); nav(`/ticket/${ref.trim().toUpperCase()}?code=${code.trim().toUpperCase()}`); }}
      className="bg-white border border-neutral-200 rounded-2xl p-5 flex flex-col sm:flex-row gap-2 items-end" data-testid="ticket-track-form">
      <div className="flex-1 w-full"><label className="block text-xs font-semibold text-neutral-600 mb-1">Référence</label><input required className={inputCls} placeholder="PS-XXXXXX" value={ref} onChange={(e) => setRef(e.target.value)} data-testid="track-ref-input" /></div>
      <div className="flex-1 w-full"><label className="block text-xs font-semibold text-neutral-600 mb-1">Code d'accès (reçu par email)</label><input required className={inputCls} placeholder="6 caractères" value={code} onChange={(e) => setCode(e.target.value)} data-testid="track-code-input" /></div>
      <button className="bg-neutral-900 hover:bg-neutral-800 text-white font-semibold px-4 py-2.5 rounded-md flex items-center gap-2 text-sm" data-testid="track-submit-btn"><Search size={15} /> Suivre</button>
    </form>
  );
};

const TicketForm = () => {
  const [meta, setMeta] = useState({ types: [], severities: [] });
  const [f, setF] = useState({ nom: "", email: "", telephone: "", societe: "", type: "qualite", severity: "NORMAL", subject: "", message: "", website: "" });
  const [known, setKnown] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { getTicketTypes().then((r) => setMeta(r.data)).catch(log.error); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const lookup = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) return;
    try {
      const { data } = await lookupTicketEmail(f.email);
      setKnown(data);
      if (data.known && data.societe && !f.societe) setF((x) => ({ ...x, societe: data.societe }));
    } catch (e) { log.error(e); }
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await createPublicTicket(f); setDone(data); }
    catch (ex) { const d = ex?.response?.data?.detail; setErr(typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" · ") : "Envoi impossible, réessayez dans un instant."); }
    finally { setBusy(false); }
  };

  if (done) return (
    <div className="bg-white border border-emerald-200 rounded-2xl p-8 text-center" data-testid="ticket-success">
      <CheckCircle2 size={44} className="text-emerald-600 mx-auto mb-3" />
      <h2 className="text-xl font-bold text-neutral-900">Ticket enregistré</h2>
      <p className="text-neutral-600 mt-2">Référence <b data-testid="ticket-success-ref">{done.ref}</b> · code d'accès <b data-testid="ticket-success-code">{done.code}</b></p>
      <p className="text-sm text-neutral-500 mt-1">{done.email_sent ? "Un email de confirmation avec le lien de suivi vous a été envoyé." : "Notez bien ces informations : elles sont nécessaires pour le suivi."}</p>
      {!done.dolibarr_synced && <p className="text-xs text-amber-700 mt-2">Votre ticket sera transmis à notre outil de gestion dans quelques minutes.</p>}
      <Link to={`/ticket/${done.ref}?code=${done.code}`} className="inline-block mt-5 bg-brand hover:bg-brand-dark text-white font-semibold px-5 py-2.5 rounded-md" data-testid="ticket-success-track">Suivre mon ticket</Link>
    </div>
  );

  return (
    <form onSubmit={submit} className="bg-white border border-neutral-200 rounded-2xl p-6 grid sm:grid-cols-2 gap-4" data-testid="ticket-form">
      <div><label className="block text-xs font-semibold text-neutral-600 mb-1">Votre nom *</label><input required className={inputCls} value={f.nom} onChange={set("nom")} data-testid="ticket-nom" /></div>
      <div><label className="block text-xs font-semibold text-neutral-600 mb-1">Email *</label><input required type="email" className={inputCls} value={f.email} onChange={set("email")} onBlur={lookup} data-testid="ticket-email" />
        {known?.known && <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1" data-testid="ticket-known"><Building2 size={12} /> Client reconnu : {known.societe}</p>}</div>
      <div><label className="block text-xs font-semibold text-neutral-600 mb-1">Téléphone</label><input className={inputCls} value={f.telephone} onChange={set("telephone")} data-testid="ticket-telephone" /></div>
      <div><label className="block text-xs font-semibold text-neutral-600 mb-1">Société / site</label><input className={inputCls} value={f.societe} onChange={set("societe")} data-testid="ticket-societe" /></div>
      <div><label className="block text-xs font-semibold text-neutral-600 mb-1">Type de demande *</label>
        <select className={inputCls} value={f.type} onChange={set("type")} data-testid="ticket-type">{meta.types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}</select></div>
      <div><label className="block text-xs font-semibold text-neutral-600 mb-1">Urgence</label>
        <select className={inputCls} value={f.severity} onChange={set("severity")} data-testid="ticket-severity">{meta.severities.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}</select></div>
      <div className="sm:col-span-2"><label className="block text-xs font-semibold text-neutral-600 mb-1">Objet *</label><input required minLength={3} className={inputCls} value={f.subject} onChange={set("subject")} data-testid="ticket-subject" /></div>
      <div className="sm:col-span-2"><label className="block text-xs font-semibold text-neutral-600 mb-1">Description *</label><textarea required minLength={10} rows={5} className={inputCls} value={f.message} onChange={set("message")} placeholder="Décrivez la situation : lieu, date, constat…" data-testid="ticket-message" /></div>
      <input type="text" value={f.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      {err && <p className="sm:col-span-2 text-sm text-red-600" data-testid="ticket-error">{err}</p>}
      <button type="submit" disabled={busy} className="sm:col-span-2 bg-brand hover:bg-brand-dark text-white font-semibold py-3 rounded-md flex items-center justify-center gap-2 disabled:opacity-60" data-testid="ticket-submit">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <LifeBuoy size={16} />} Envoyer ma demande</button>
      <p className="sm:col-span-2 text-xs text-neutral-500">Vous recevrez un email avec la référence et un code d'accès pour suivre le traitement et échanger avec notre équipe.</p>
    </form>
  );
};

export default function Ticket() {
  const { ref } = useParams();
  const [params] = useSearchParams();
  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      <TopBar />
      <main className="flex-1 max-w-3xl w-full mx-auto px-5 py-10">
        {ref ? <TicketTracking ref={ref} code={params.get("code") || ""} /> : (
          <>
            <div className="mb-8">
              <p className="text-xs font-bold uppercase tracking-wider text-brand mb-2">Service client</p>
              <h1 className="text-3xl sm:text-4xl font-heading font-bold text-neutral-900">Déposer une demande ou un signalement</h1>
              <p className="text-neutral-600 mt-3 text-sm sm:text-base">Réclamation qualité, passage supplémentaire, problème d'accès, facturation… Votre ticket est transmis directement à notre équipe et vous suivez son traitement en ligne.</p>
            </div>
            <TicketForm />
            <h2 className="text-base md:text-lg font-bold text-neutral-900 mt-10 mb-3">Vous avez déjà un ticket ?</h2>
            <TrackForm />
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
